/**
 * Home screen — RPG-style menu.
 * The user has HP (pending fees pressure), coins (notification count),
 * a level (role), and a list of "actions" to perform.
 */
import { useEffect, useRef, useState } from 'react';
import { useApp } from '@/state/store';
import { notificationsApi, feesApi, visitsApi, voicemailsApi, type Fee } from '@/lib/api';
import { sfx } from '@/lib/audio';
import { DoorSprite, BellSprite, MegaphoneSprite, WrenchSprite, BallotSprite, CoinSprite, UserSprite, GearSprite, QRSprite, MailSprite, CrownSprite, MicSprite } from '@/components/Sprites';
import type { Screen } from '@/state/store';

interface MenuItem {
  id: Screen;
  label: string;
  hint: string;
  Icon: typeof DoorSprite;
  badge?: number;
  adminOnly?: boolean;
  ownerOnly?: boolean;
  residentOnly?: boolean;
}

export function HomeScreen() {
  const { user, community, menuIndex, setMenuIndex, goto } = useApp();
  const [unread, setUnread] = useState(0);
  const [unreadVoicemails, setUnreadVoicemails] = useState(0);
  const [pendingFees, setPendingFees] = useState<Fee[]>([]);
  const [activeRings, setActiveRings] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [notifRes, feesRes, ringsRes, vmRes] = await Promise.allSettled([
          notificationsApi.unreadCount(),
          feesApi.list(),
          visitsApi.active(),
          voicemailsApi.unreadCount(),
        ]);
        if (!cancelled) {
          if (notifRes.status === 'fulfilled' && notifRes.value.count !== undefined) setUnread(notifRes.value.count);
          if (feesRes.status === 'fulfilled') setPendingFees(feesRes.value.fees.filter(f => f.status !== 'paid'));
          if (ringsRes.status === 'fulfilled') setActiveRings((ringsRes.value.rings || []).length);
          if (vmRes.status === 'fulfilled' && typeof vmRes.value.count === 'number') {
            setUnreadVoicemails(vmRes.value.count);
          }
        }
      } catch { /* */ }
    };
    load();
    // Polling cada 10s para detectar rings activos (game-like: urgente!)
    const id = setInterval(load, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const items: MenuItem[] = [
    { id: 'door', label: 'PUERTA', hint: 'A → abrir', Icon: DoorSprite },
    { id: 'doorbell', label: 'TIMBRE', hint: 'A → ver', Icon: BellSprite, badge: activeRings > 0 ? activeRings : undefined },
    { id: 'voicemails', label: 'MENSAJ', hint: 'A → escuchar', Icon: MicSprite, badge: unreadVoicemails > 0 ? unreadVoicemails : undefined },
    { id: 'doorbell-share', label: 'QR', hint: 'A → compartir', Icon: QRSprite },
    { id: 'announcements', label: 'NOTIC', hint: 'A → leer', Icon: MegaphoneSprite, badge: unread > 0 ? unread : undefined },
    { id: 'incidents', label: 'REPOR', hint: 'A → reportar', Icon: WrenchSprite },
    { id: 'votes', label: 'VOTOS', hint: 'A → votar', Icon: BallotSprite },
    { id: 'fees', label: 'GASTOS', hint: 'A → ver', Icon: CoinSprite, ownerOnly: true },
    { id: 'admin', label: 'ADMIN', hint: 'A → admin', Icon: CrownSprite, adminOnly: true },
    { id: 'profile', label: 'PERFIL', hint: 'A → ver', Icon: UserSprite },
    { id: 'settings', label: 'AJUSTES', hint: 'A → ver', Icon: GearSprite },
  ];

  const visible = items.filter((it) => {
    if (it.adminOnly && user?.role !== 'admin') return false;
    if (it.ownerOnly && user?.role !== 'owner' && user?.role !== 'admin') return false;
    if (it.residentOnly && user?.role !== 'resident') return false;
    return true;
  });

  useEffect(() => {
    if (menuIndex >= visible.length) setMenuIndex(Math.max(0, visible.length - 1));
  }, [visible.length, menuIndex, setMenuIndex]);

  const moveSelection = (dir: 'up' | 'down' | 'left' | 'right') => {
    if (visible.length === 0) return;
    const cols = 3;
    const total = visible.length;
    const row = Math.floor(menuIndex / cols);
    const col = menuIndex % cols;
    let next = menuIndex;
    if (dir === 'up' && row > 0) next = menuIndex - cols;
    else if (dir === 'down' && menuIndex + cols < total) next = menuIndex + cols;
    else if (dir === 'left' && col > 0) next = menuIndex - 1;
    else if (dir === 'right' && col < cols - 1 && menuIndex + 1 < total) next = menuIndex + 1;
    if (next !== menuIndex) {
      setMenuIndex(next);
      sfx.tick();
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="home-item-${visible[next]?.id}"]`) as HTMLElement | null;
        if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }, 0);
    }
  };

  const activate = () => {
    const it = visible[menuIndex];
    if (!it) return;
    sfx.confirm();
    goto(it.id);
  };

  // D-pad + A hardware buttons
  const handlersRef = useRef({ moveSelection, activate, goto });
  handlersRef.current = { moveSelection, activate, goto };

  useEffect(() => {
    const onUp = () => handlersRef.current.moveSelection('up');
    const onDown = () => handlersRef.current.moveSelection('down');
    const onLeft = () => handlersRef.current.moveSelection('left');
    const onRight = () => handlersRef.current.moveSelection('right');
    const onA = () => handlersRef.current.activate();
    const onB = () => { /* B on home = no-op */ };
    window.addEventListener('vicin-gb:up', onUp);
    window.addEventListener('vicin-gb:down', onDown);
    window.addEventListener('vicin-gb:left', onLeft);
    window.addEventListener('vicin-gb:right', onRight);
    window.addEventListener('vicin-gb:a', onA);
    window.addEventListener('vicin-gb:b', onB);
    return () => {
      window.removeEventListener('vicin-gb:up', onUp);
      window.removeEventListener('vicin-gb:down', onDown);
      window.removeEventListener('vicin-gb:left', onLeft);
      window.removeEventListener('vicin-gb:right', onRight);
      window.removeEventListener('vicin-gb:a', onA);
      window.removeEventListener('vicin-gb:b', onB);
    };
  }, []);

  return (
    <div className="gb-screen">
      <div className="gb-status">
        <div className="gb-status-left">
          <span className="gb-status-dot" />
          <span className="gb-pix gb-pix-xs">{community?.name?.slice(0, 12) || 'VICIN'}</span>
        </div>
        <div className="gb-status-right">
          {unread > 0 && <span className="gb-pix gb-pix-xs gb-blink" data-testid="home-unread-badge">●{unread}</span>}
        </div>
      </div>

      <div className="gb-body" style={{ padding: '4px 4px', gap: 4, overflow: 'hidden' }}>
        {/* URGENT banner — visible cuando alguien está timbrando */}
        {activeRings > 0 && (
          <div
            className="gb-dialog gb-blink"
            style={{
              padding: '3px 6px',
              background: 'var(--gb-dark)',
              color: 'var(--gb-bg)',
              border: '2px dashed var(--gb-deepest)',
              borderRadius: 3,
              cursor: 'pointer',
              flexShrink: 0,
            }}
            onClick={() => { sfx.confirm(); goto('doorbell'); }}
            data-testid="home-urgent-banner"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') { sfx.confirm(); goto('doorbell'); } }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <BellSprite size={14} />
              <span className="gb-pix" style={{ fontSize: 7, color: '#9bbc0f' }}>
                ¡TIMBRE! {activeRings} {activeRings === 1 ? 'VISITANTE' : 'VISITANTES'}
              </span>
            </div>
          </div>
        )}

        {/* Compact user line */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            padding: '0 2px',
            flexShrink: 0,
          }}
          data-testid="home-user-line"
        >
          <UserSprite size={11} />
          <span
            className="gb-pix"
            style={{
              flex: 1,
              fontSize: 7,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: 'var(--gb-dark)',
            }}
          >
            {user?.name || user?.email?.split('@')[0] || 'VECINO'}
          </span>
          <span
            className="gb-pix gb-mid-text"
            style={{ fontSize: 5, letterSpacing: 0.5 }}
          >
            {roleLabel(user?.role)}
          </span>
          {(pendingFees.length > 0 || unread > 0 || unreadVoicemails > 0) && (
            <span
              className="gb-pix"
              style={{
                fontSize: 6,
                display: 'flex',
                gap: 3,
                alignItems: 'center',
              }}
            >
              {pendingFees.length > 0 && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: 'var(--gb-dark)' }}>
                  <CoinSprite size={8} />
                  {pendingFees.length}
                </span>
              )}
              {unreadVoicemails > 0 && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: 'var(--gb-dark)' }}>
                  <MicSprite size={8} />
                  {unreadVoicemails}
                </span>
              )}
              {unread > 0 && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: 'var(--gb-dark)' }}>
                  <MailSprite size={8} />
                  {unread}
                </span>
              )}
            </span>
          )}
        </div>

        {/* Grid — icon tiles with labels at 7px (was 5px, was unreadable) */}
        <div className="gb-home-grid">
          {visible.map((it, i) => {
            const selected = i === menuIndex;
            return (
              <button
                key={it.id}
                className="gb-home-cell"
                data-selected={selected}
                aria-current={selected ? 'true' : undefined}
                onClick={() => { setMenuIndex(i); sfx.confirm(); goto(it.id); }}
                onMouseEnter={() => setMenuIndex(i)}
                data-testid={`home-item-${it.id}`}
              >
                <div className="gb-home-cell-icon">
                  <it.Icon size={20} />
                  {it.badge !== undefined && (
                    <span className="gb-blink gb-home-cell-badge">
                      {it.badge}
                    </span>
                  )}
                </div>
                <span className="gb-home-cell-label">
                  {it.label}
                </span>
              </button>
            );
          })}
        </div>

        <div className="gb-pix gb-home-hint">
          [▲▼◀▶] NAVEGAR · [A] ENTRAR · [B] VOLVER
        </div>
      </div>
    </div>
  );
}

function roleLabel(role?: string): string {
  switch (role) {
    case 'admin': return 'PRESIDENTE';
    case 'owner': return 'PROPIETARIO';
    case 'resident': return 'INQUILINO';
    case 'member': return 'VECINO';
    default: return 'VECINO';
  }
}
